import React from 'react';
import { useMemo } from 'react';

import { ScrollView } from '@rbilabs/universal-components';

import { useAppTakeoverPdp } from 'components/app-takeover/use-app-takeover';
import { InnerLayout, LayoutContainer } from 'components/layout/styled';
import { FullWidthAutoHeightImage } from 'components/picture';
import { useIsMobileBreakpoint } from 'hooks/use-media-query';
import { t } from 'state/intl/translations';
import { useUnifiedMenuContext } from 'state/unified-menu/unified-menu-context';
import { isCombo } from 'state/unified-menu/utils';

import { useProductWizardContext } from '../../product-wizard';
import { useProductWizardFlow } from '../../product-wizard/use-product-wizard-flow';
import { ProductCta } from '../product-cta';
import { ProductDetailHeader } from '../product-detail-flow/product-detail-header';
import { getEventAttributesForCustomization } from '../utils';

import { ProductCustomizationDrinks } from './product-customization-drinks';
import { ProductCustomizationModifiers } from './product-customization-modifiers';
import { ProductCustomizationSides } from './product-customization-sides';
import { ProductCustomizationPanel } from './styled';
import { ComboSlotType } from './types';
import { getComboSlotType } from './utils';

export const ProductCustomization = () => {
  const isMobile = useIsMobileBreakpoint();

  const { indexedMenu } = useUnifiedMenuContext();
  const { currentFlowStep, id } = useProductWizardFlow();
  const { backgroundColor: takeoverBg, backgroundImage: takeoverBgImage } = useAppTakeoverPdp(id);
  const { menuObject, selectedProduct } = useProductWizardContext();

  const comboSlotType: ComboSlotType = useMemo(
    () => getComboSlotType(indexedMenu, currentFlowStep.slotKey!),
    [indexedMenu, currentFlowStep]
  );

  const getHeaderTitle = () => {
    let title = t('haveItYourWay');
    const productName = selectedProduct.name?.locale ?? '';

    if (isCombo(menuObject) && productName) {
      title = productName;
    } else if (comboSlotType === ComboSlotType.SIDES) {
      title = t('chooseSide');
    } else if (comboSlotType === ComboSlotType.DRINKS) {
      title = t('chooseDrink');
    }

    return title;
  };

  // SIDES & DRINKS have an inline ProductCustomizationModifiers
  const shouldRenderModifiers = useMemo(
    () => ![ComboSlotType.SIDES, ComboSlotType.DRINKS].includes(comboSlotType),
    [comboSlotType]
  );

  const eventAttributes = useMemo(() => {
    return getEventAttributesForCustomization({
      currentStep: currentFlowStep.step,
      menuObjectType: menuObject.type,
      comboSlotType,
    });
  }, [comboSlotType, currentFlowStep.step, menuObject.type]);

  return (
    <ProductCustomizationPanel>
      <ProductDetailHeader title={getHeaderTitle()} eventAttributes={eventAttributes ?? {}} />
      <ScrollView showsVerticalScrollIndicator={!isMobile} backgroundColor={takeoverBg}>
        {takeoverBgImage && (
          <FullWidthAutoHeightImage source={takeoverBgImage} style={{ position: 'absolute' }} />
        )}
        <LayoutContainer isFullContainer={isMobile}>
          <InnerLayout>
            {comboSlotType === ComboSlotType.DRINKS ? (
              <ProductCustomizationDrinks />
            ) : (
              <ProductCustomizationSides shouldRenderModifiers={!shouldRenderModifiers} />
            )}
            {shouldRenderModifiers && <ProductCustomizationModifiers />}
          </InnerLayout>
        </LayoutContainer>
      </ScrollView>
      <ProductCta />
    </ProductCustomizationPanel>
  );
};
